import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Container from "../../common/Container";

/**
 * To add photos:
 *   1. Run:  bash scripts/compress-hero.sh ~/your-photo.jpg public/memorial/your-photo.webp
 *   2. Add an entry to PHOTOS below.
 */
const PHOTOS: { src: string; caption: string; hidden?: boolean }[] = [
  { src: "/memorial/Screenshot_20220808-222953.webp", caption: "Bobby" },
  { src: "/memorial/DSC_3166.webp", caption: "Bringing the wing up" },
  { src: "/memorial/DSC_3167.webp", caption: "Takeoff" },
  { src: "/memorial/received_215871776500520~2.webp", caption: "Blue skies and sunsets" },
  { src: "/memorial/PXL_20220731_235820516.webp", caption: "Preflighting a new motor" },
  { src: "/memorial/20210710_073650-PANO.webp", caption: "Above the morning fog" },
  { src: "/memorial/20220827_074850.webp", caption: "" },
  { src: "/memorial/IMG_1921.webp", caption: "" },
  { src: "/memorial/IMG_5166.webp", caption: "" },
  { src: "/memorial/PXL_20220807_204744585.webp", caption: "", hidden: true },
  { src: "/memorial/Resized_20220615_203400.webp", caption: "" },

  // { src: "/memorial/robert-flying.webp",  caption: "Robert on a morning flight" },
  // { src: "/memorial/group.webp",           caption: "The crew at Burrell" },
];

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  margin-top: 1rem;
`;

const ClickableFigure = styled.figure`
  margin: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  cursor: zoom-in;

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
    transition: transform 0.2s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }

  figcaption {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    color: var(--color-muted, #666);
  }
`;

/* ── Lightbox ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  /* prevent body scroll */
  overscroll-behavior: contain;
`;

const LightboxImg = styled.img`
  max-width: min(92vw, 1200px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: 4px;
  user-select: none;
  -webkit-user-drag: none;
`;

const LightboxCaption = styled.p`
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  text-align: center;
  margin: 0;
  padding: 0 1rem;
  max-width: 600px;
`;

const NavBtn = styled.button<{ side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ side }) => side}: 1rem;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  font-size: 2rem;
  line-height: 1;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  opacity: 0.75;
  &:hover {
    opacity: 1;
  }
`;

const Counter = styled.span`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
`;

const Hero = styled.div`
  background:
    linear-gradient(rgba(3, 105, 161, 0.55), rgba(3, 105, 161, 0.55)),
    url("/memorial/Screenshot_20220808-222953.webp") center / cover no-repeat;
  color: #fff;
  padding: 5rem 1.25rem;
  text-align: center;
  border-radius: 8px;
  margin-bottom: 2rem;

  h1 {
    color: #fff;
    font-size: clamp(1.75rem, 4vw, 3rem);
    margin: 0 0 0.5rem;
  }

  p {
    margin: 0;
    opacity: 0.85;
    font-size: 1.05rem;
  }
`;

const Placeholder = styled.div`
  background: var(--color-surface, #f4f4f4);
  border: 2px dashed var(--color-border, #ccc);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  color: var(--color-muted, #888);
  font-size: 0.9rem;
  margin-top: 1rem;

  code {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: var(--color-primary, #0b3d2e);
  }
`;

function Memorial() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const close = useCallback(() => setActiveIdx(null), []);
  const prev = useCallback(
    () => setActiveIdx((i) => (i === null ? null : (i - 1 + PHOTOS.length) % PHOTOS.length)),
    [],
  );
  const next = useCallback(
    () => setActiveIdx((i) => (i === null ? null : (i + 1) % PHOTOS.length)),
    [],
  );

  // Keyboard navigation
  useEffect(() => {
    if (activeIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, close, prev, next]);

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = activeIdx !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeIdx]);

  return (
    <Container>
      <Hero>
        <h1>In memory of Robert Durbin</h1>
        <p>Western PA Paramotors</p>
      </Hero>
      <p>
        Bobby had a gift for bringing people together to live life to the fullest.
        From establishing snowboarding ramps and rails at Peek'n Peak, to teaching people how to wakeboard on the river, to building a group to paramotor with- he knew that everything fun is better with a community. 
        Bobby knew how to calculate and manage risk better than anyone, and his safety-first mindset has shaped how we fly and the culture of our group. We miss him every time we take to the sky.
        
        Here are a few lessons we can carry with us in his memory:
      </p>
      <ul>
        <li>Plan the flight, and fly the plan. It's not a calculated risk if you make it up as you go.</li>
        <li>Always seek out mentorship and learn from those with more experience. There's always someone who can teach you something new, and a good pilot is always learning.</li>
        <li>Take care of your gear, and it will take care of you. Preflight thoroughly, and don't push things beyond their limits.</li>
        <li>Fly with friends whenever you can. It's more fun, and safer too.</li>
      </ul>

      <h2>Watch</h2>
      <p>A few of our favorite videos featuring Robert.</p>
      <div
        style={{
          position: "relative",
          paddingTop: "56.25%",
          maxWidth: 720,
          marginTop: "1rem",
        }}
      >
        <iframe
          title="Planning a flight over water."
          src="https://www.youtube-nocookie.com/embed/mKron8_mzEo"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </div>

      <div
        style={{
          position: "relative",
          paddingTop: "56.25%",
          maxWidth: 720,
          marginTop: "1rem",
        }}
      >
        <iframe
          title="Robert Durbin featured on Tucker Gott's channel"
          src="https://www.youtube-nocookie.com/embed/Lmsrj98JHmE"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </div>
      <h2>Photos</h2>

      {PHOTOS.length > 0 ? (
        <Grid>
          {PHOTOS.map(({ src, caption, hidden }, idx) => hidden ? null : (
            <ClickableFigure key={src} onClick={() => setActiveIdx(idx)}>
              <img src={src} alt={caption} loading="lazy" />
              {caption && <figcaption>{caption}</figcaption>}
            </ClickableFigure>
          ))}
        </Grid>
      ) : (
        <Placeholder>
          No photos yet — add some!
          <code>
            bash scripts/compress-hero.sh ~/your-photo.jpg public/memorial/your-photo.webp
          </code>
          Then add an entry to the PHOTOS array in this file.
        </Placeholder>
      )}

      {/* ── Lightbox ── */}
      {activeIdx !== null && (
        <Overlay
          onClick={close}
          onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
            touchStartX.current = null;
          }}
        >
          {/* Stop click-inside-image from closing the overlay */}
          <div onClick={(e) => e.stopPropagation()}>
            <LightboxImg
              src={PHOTOS[activeIdx].src}
              alt={PHOTOS[activeIdx].caption}
            />
          </div>

          <Counter>{activeIdx + 1} / {PHOTOS.length}</Counter>

          {PHOTOS[activeIdx].caption && (
            <LightboxCaption>{PHOTOS[activeIdx].caption}</LightboxCaption>
          )}

          {PHOTOS.length > 1 && (
            <>
              <NavBtn side="left" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">
                ‹
              </NavBtn>
              <NavBtn side="right" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">
                ›
              </NavBtn>
            </>
          )}

          <CloseBtn onClick={close} aria-label="Close">×</CloseBtn>
        </Overlay>
      )}
    </Container>
  );
}

export default Memorial;
